
const AtelierExperience = () => {
  return (
    <section className="bg-warmWhite">
      {/* Image + text side by side */}
      <div className="max-w-7xl mx-auto px-5 sm:px-10 lg:px-24 py-10 sm:py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
        {/* Image */}
        <div className="relative w-full h-[200px] sm:h-[300px] lg:h-[480px] overflow-hidden rounded-lg mx-auto">
          <img
            src="https://public.readdy.ai/ai/img_res/6218fb571d30ecf6ac81c12e8b3c06d3.jpg"
            alt="Vionne Stability Atelier Interior"
            className="w-full h-full object-cover object-top"
          />
        </div>

        {/* Text content */}
        <div>
          <div className="flex items-center gap-2 mb-4 sm:mb-6">
            <div className="w-5 sm:w-6 h-[1px] bg-charcoal/30"></div>
            <span className="text-charcoal/50 text-[9px] sm:text-[10px] font-sans font-semibold tracking-[0.3em] uppercase">
              The Space
            </span>
          </div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-charcoal italic leading-snug mb-5 sm:mb-8">
            The Atelier<br />Experience
          </h2>
          <div className="space-y-2.5 sm:space-y-3 border-l border-charcoal/15 pl-4 sm:pl-5">
            <p className="text-[12px] sm:text-[13px] text-charcoal/60 leading-[1.8] sm:leading-[1.9] font-sans font-light">
              A quiet space for structural regulation.
            </p>
            <p className="text-[12px] sm:text-[13px] text-charcoal/60 leading-[1.8] sm:leading-[1.9] font-sans font-light">
              Vionne Clinical Atelier is designed to support clarity—for both practitioner and client.
              Natural light, softened textures, and subtle botanical notes create a calm environment where the nervous system can begin to settle without effort.
            </p>
            <p className="text-[12px] sm:text-[13px] text-charcoal/80 leading-[1.8] sm:leading-[1.9] font-sans font-medium italic">
              This is not a space of excess.<br />
              It is a space of attentiveness.
            </p>
            <p className="text-[12px] sm:text-[13px] text-charcoal/60 leading-[1.8] sm:leading-[1.9] font-sans font-light">
              Each session allows time for careful observation — of breath, tone, posture, and the subtle ways prolonged responsibility shapes the face.
            </p>
            <p className="text-[12px] sm:text-[13px] text-charcoal/80 leading-[1.8] sm:leading-[1.9] font-sans font-medium">
              This is neither a spa nor a clinical setting.<br />
              It is a refined atelier — where understanding comes before intervention.
            </p>
          </div>
        </div>
      </div>

      {/* Method CTA */}
      <div className="px-5 sm:px-10 lg:px-24 py-10 sm:py-12 lg:py-14 border-t border-charcoal/8">
        <div className="max-w-7xl mx-auto flex flex-col items-center justify-center gap-6 text-center">
          <p className="font-display text-base sm:text-lg md:text-xl font-light text-charcoal/80 italic leading-snug whitespace-nowrap">
            The clinical framework of this work is expressed through the Vionne Stability Method
          </p>
          <a
            href="/method"
            className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 border border-charcoal/25 text-charcoal/70 text-[11px] sm:text-[12px] font-sans font-medium tracking-[0.15em] uppercase hover:bg-charcoal hover:text-warmWhite transition-all duration-300 rounded-sm cursor-pointer"
          >
            Explore the Method
            <i className="ri-arrow-right-line text-sm"></i>
          </a>
        </div>
      </div>

      {/* Location & Hours */}
      <div className="bg-stone-100 px-5 sm:px-10 lg:px-24 py-8 sm:py-10 lg:py-12">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-10 items-start">
          <div className="lg:col-span-2">
            <p className="font-display text-xl sm:text-2xl font-light text-charcoal/80 italic leading-snug">
              Visit the Atelier
            </p>
            <p className="text-[11px] sm:text-[12px] text-charcoal/40 font-sans font-light mt-1.5 sm:mt-2 tracking-wide">
              Vancouver, BC — By appointment
            </p>
          </div>

          <div className="flex items-start gap-3 sm:gap-4">
            <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full border border-charcoal/15 flex-shrink-0 mt-0.5">
              <i className="ri-map-pin-line text-softGold text-sm"></i>
            </div>
            <div>
              <p className="text-[9px] sm:text-[10px] text-charcoal/40 font-sans tracking-[0.2em] uppercase mb-1.5 sm:mb-2">Location</p>
              <p className="text-[12px] sm:text-[13px] text-charcoal/65 font-sans font-light leading-relaxed">
                401-9 1388 West Broadway<br />
                Vancouver, BC V6H1H2
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 sm:gap-4">
            <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full border border-charcoal/15 flex-shrink-0 mt-0.5">
              <i className="ri-time-line text-softGold text-sm"></i>
            </div>
            <div>
              <p className="text-[9px] sm:text-[10px] text-charcoal/40 font-sans tracking-[0.2em] uppercase mb-1.5 sm:mb-2">Hours</p>
              <div className="space-y-1 sm:space-y-1.5">
                <div className="flex justify-between gap-6 sm:gap-8">
                  <span className="text-[11px] sm:text-[12px] text-charcoal/40 font-sans font-light">Mon &amp; Sun</span>
                  <span className="text-[11px] sm:text-[12px] text-charcoal/30 font-sans font-light">Closed</span>
                </div>
                <div className="flex justify-between gap-6 sm:gap-8">
                  <span className="text-[11px] sm:text-[12px] text-charcoal/60 font-sans font-light">Tue &amp; Wed</span>
                  <span className="text-[11px] sm:text-[12px] text-charcoal/60 font-sans font-light">12:30 – 7:30</span>
                </div>
                <div className="flex justify-between gap-6 sm:gap-8">
                  <span className="text-[11px] sm:text-[12px] text-charcoal/60 font-sans font-light">Thu – Sat</span>
                  <span className="text-[11px] sm:text-[12px] text-charcoal/60 font-sans font-light">8:00 – 5:00</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AtelierExperience;
